import { useState, useCallback, useEffect, useRef } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { fetchFastApi } from "../api/fastapi";
import { FeatureFlag, useFeatureFlag } from "../contexts/launchDarklyContext";
import { useAudit } from "../contexts/auditContext";
import type { SessionKind } from "./audit/useControlSessionEnvelope";
import { workPaperTemplateApiBase } from "../lib/workPaperTemplateApi";

const GenerationStatusResponseSchema = z.object({
  runId: z.string().nullable(),
  state: z.enum(["queued", "running", "succeeded", "failed"]).nullable(),
  fieldsGenerated: z.number().nullable(),
});

interface UseGenerateFieldValuesOptions {
  /** Template x session to rejoin an in-flight generation for on mount. */
  sessionId?: string | null;
  templateId?: string | null;
  /** Called when a rejoined run finishes successfully, to refresh field state. */
  onGenerated?: () => void;
}

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_INTERVAL_MS = 30000;
const MAX_CONSECUTIVE_POLL_FAILURES = 5;

// Covers the worker's own ceiling (15m execution + 5m scheduling). Past that the
// run cannot still be alive, so a client still polling is waiting on nothing.
const POLL_TIMEOUT_MS = 21 * 60 * 1000;

const statusPath = (
  sessionKind: SessionKind,
  sessionId: string,
  templateId: string,
) =>
  `${workPaperTemplateApiBase(sessionKind, sessionId, templateId)}/generate/status`;

/** `cancelled` means the caller went away: report nothing, toast nothing. */
type PollOutcome = "succeeded" | "failed" | "cancelled";

/**
 * Poll the generation running for a template x session until it settles.
 *
 * The run is not named in the request — the backend resolves whichever run is
 * current for this template x session, so a client that lost its run id (a
 * reload) rejoins by asking the same question.
 *
 * Self-scheduling rather than a fixed loop, so the next request is only queued
 * once the current one settles and the cadence can back off on repeated
 * failures (3s → 6s → 12s … capped at 30s). Never rejects: every outcome,
 * including a run that failed and a status endpoint that will not answer,
 * comes back as a `PollOutcome`, so a caller cannot leak an unhandled
 * rejection and skip its own cleanup.
 */
function pollGeneration(
  sessionKind: SessionKind,
  sessionId: string,
  templateId: string,
  isCancelled: () => boolean,
): Promise<PollOutcome> {
  return new Promise<PollOutcome>((resolve) => {
    const deadline = Date.now() + POLL_TIMEOUT_MS;
    let consecutiveFailures = 0;

    const scheduleNext = (delayMs: number) => {
      if (isCancelled()) return resolve("cancelled");
      if (Date.now() >= deadline) {
        console.error("Timed out waiting for field value generation", {
          sessionId,
          templateId,
        });
        return resolve("failed");
      }
      // A timer pending at cancellation fires once and resolves "cancelled"
      // without touching the network, so it needs no separate teardown.
      setTimeout(() => void poll(), delayMs);
    };

    const poll = async () => {
      if (isCancelled()) return resolve("cancelled");
      try {
        const response = await fetchFastApi({
          path: statusPath(sessionKind, sessionId, templateId),
          method: "GET",
        });
        if (isCancelled()) return resolve("cancelled");

        // Parsed, not cast: an unrecognised state would otherwise fall through
        // the checks below and keep the poll alive for the full timeout before
        // failing. A shape we do not understand fails now, in the catch.
        const status = GenerationStatusResponseSchema.parse(response);
        consecutiveFailures = 0;

        if (status.state === "succeeded") return resolve("succeeded");
        // `failed`, and `null` for a run the backend no longer has on record —
        // from here both mean the same thing: no new values are coming.
        if (status.state === "failed" || status.state === null) {
          return resolve("failed");
        }
        scheduleNext(POLL_INTERVAL_MS);
      } catch (error) {
        console.error("Failed to poll field value generation status:", error);
        if (isCancelled()) return resolve("cancelled");
        consecutiveFailures += 1;
        if (consecutiveFailures >= MAX_CONSECUTIVE_POLL_FAILURES) {
          return resolve("failed");
        }
        scheduleNext(
          Math.min(
            POLL_INTERVAL_MS * 2 ** consecutiveFailures,
            MAX_POLL_INTERVAL_MS,
          ),
        );
      }
    };

    scheduleNext(POLL_INTERVAL_MS);
  });
}

export function useGenerateFieldValues(
  options: UseGenerateFieldValuesOptions = {},
) {
  const { sessionId, templateId, onGenerated } = options;
  const { sessionKind } = useAudit();
  const [generating, setGenerating] = useState(false);
  const asyncGeneration = useFeatureFlag(FeatureFlag.templateGenerationAsync);

  // Held in a ref so a caller passing an inline callback does not re-trigger
  // the rejoin effect on every render.
  const onGeneratedRef = useRef(onGenerated);
  useEffect(() => {
    onGeneratedRef.current = onGenerated;
  });

  // Whichever template x session the page is currently showing. Both polling
  // paths compare against it, so navigating away or switching template ends the
  // poll instead of leaving it to run out its timeout and toast over whatever
  // the user is looking at by then.
  const activeTargetRef = useRef<string | null>(null);
  useEffect(() => {
    activeTargetRef.current =
      sessionId && templateId ? `${sessionId}:${templateId}` : null;
    return () => {
      activeTargetRef.current = null;
    };
  }, [sessionId, templateId]);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const generateValues = useCallback(
    async (
      sessionId: string,
      templateId: string,
      fieldIds?: string[],
    ): Promise<boolean> => {
      setGenerating(true);
      try {
        const body: Record<string, string[]> = fieldIds ? { fieldIds } : {};

        if (!asyncGeneration) {
          // Legacy path: one request held open for the whole run, so a slow
          // generation can be cut short by the gateway even though the run
          // itself continues. Removed once the flag is fully rolled out.
          await fetchFastApi({
            path: `${workPaperTemplateApiBase(sessionKind, sessionId, templateId)}/generate`,
            method: "POST",
            body,
          });

          toast.success("Field values generated successfully");
          return true;
        }

        await fetchFastApi({
          path: `${workPaperTemplateApiBase(sessionKind, sessionId, templateId)}/generate/async`,
          method: "POST",
          body,
        });

        const target = `${sessionId}:${templateId}`;
        const outcome = await pollGeneration(
          sessionKind,
          sessionId,
          templateId,
          () => {
            if (!mountedRef.current) return true;
            // A hook used without `sessionId`/`templateId` tracks no target;
            // the click still knows which one it started, so only an actual
            // switch cancels it.
            const active = activeTargetRef.current;
            return active !== null && active !== target;
          },
        );

        // The page moved on; the run is unaffected and whoever is looking at
        // this template next rejoins it on mount.
        if (outcome === "cancelled") return false;

        if (outcome === "failed") {
          toast.error("Failed to generate field values");
          return false;
        }

        toast.success("Field values generated successfully");
        return true;
      } catch (error) {
        console.error("Error generating field values:", error);
        toast.error("Failed to generate field values");
        return false;
      } finally {
        setGenerating(false);
      }
    },
    [asyncGeneration, sessionKind],
  );

  // Rejoin a generation that is still running, so a reload or a trip to another
  // page does not leave the user on stale values with no indication that work is
  // in flight. The run itself is unaffected by any of that — it belongs to the
  // worker — but the poll loop lives on this page and dies with it.
  useEffect(() => {
    if (!asyncGeneration || !sessionId || !templateId) return;

    const target = `${sessionId}:${templateId}`;
    const isCancelled = () => activeTargetRef.current !== target;

    const rejoin = async () => {
      try {
        const response = await fetchFastApi({
          path: statusPath(sessionKind, sessionId, templateId),
          method: "GET",
        });
        if (isCancelled()) return;
        const status = GenerationStatusResponseSchema.parse(response);

        // Only a live run is worth rejoining. A terminal one has already been
        // reported to whoever started it, and re-announcing it here would toast
        // at someone who has just opened the page.
        if (
          status.runId === null ||
          status.state === null ||
          status.state === "succeeded" ||
          status.state === "failed"
        ) {
          return;
        }

        setGenerating(true);
        try {
          const outcome = await pollGeneration(
            sessionKind,
            sessionId,
            templateId,
            isCancelled,
          );
          if (outcome === "cancelled") return;

          if (outcome === "succeeded") {
            toast.success("Field values generated successfully");
            onGeneratedRef.current?.();
          } else {
            toast.error("Failed to generate field values");
          }
        } finally {
          // Always cleared, including on cancellation: switching template
          // re-runs this effect, and leaving the flag set would disable the
          // generate button until a page reload.
          setGenerating(false);
        }
      } catch (error) {
        // A background check nobody asked for: log it, but never surface it.
        console.error("Could not check for an in-flight generation:", error);
      }
    };

    void rejoin();
  }, [asyncGeneration, sessionId, templateId, sessionKind]);

  return {
    generating,
    generateValues,
  };
}
